import { Mic, Square, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { WaveformVisualizer } from './WaveformVisualizer';

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

interface AudioRecorderProps {
  onRecorded: (file: File) => void;
  disabled?: boolean;
}

export function AudioRecorder({ onRecorded, disabled }: AudioRecorderProps) {
  const { state, audioFile, durationMs, amplitudes, error, startRecording, stopRecording, reset } =
    useAudioRecorder();

  const handleStop = () => {
    stopRecording();
  };

  const handleUse = () => {
    if (audioFile) onRecorded(audioFile);
  };

  return (
    <div className="flex flex-col items-center gap-4 p-4 rounded-lg border bg-card">
      <WaveformVisualizer amplitudes={amplitudes} isActive={state === 'recording'} />

      {state === 'idle' && (
        <Button onClick={startRecording} disabled={disabled} variant="outline" className="gap-2">
          <Mic className="h-4 w-4 text-red-500" />
          Start Recording
        </Button>
      )}

      {state === 'requesting' && (
        <Button disabled variant="outline" className="gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Requesting microphone…
        </Button>
      )}

      {state === 'recording' && (
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm text-muted-foreground font-mono">
            {formatDuration(durationMs)}
          </span>
          <Button onClick={handleStop} variant="destructive" className="gap-2">
            <Square className="h-4 w-4" />
            Stop Recording
          </Button>
        </div>
      )}

      {state === 'recorded' && audioFile && (
        <div className="flex flex-col items-center gap-3 w-full">
          <audio controls src={URL.createObjectURL(audioFile)} className="w-full max-w-sm" />
          <div className="flex gap-2">
            <Button onClick={handleUse} className="gap-2">
              Use Recording
            </Button>
            <Button onClick={reset} variant="ghost" size="icon" title="Record again">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {state === 'error' && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-destructive">{error}</p>
          <Button onClick={reset} variant="outline" size="sm" className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      )}
    </div>
  );
}
